import React, {useState, useRef, useEffect} from 'react';
import {Box, Button, Stack, Text, Group, LoadingOverlay} from '@mantine/core';
import {Avatar, AvatarSize} from './Avatar';

type Position = {
    x: number;
    y: number;
};

type AvatarUploadProps = {
    userName: string;
    currentImageUrl?: string | null;
    size?: AvatarSize;
    onImageSelect: () => Promise<void>;
    isUploading?: boolean;
    disabled?: boolean;
};

const sizeMap: Record<AvatarSize, number> = {
    xs: 32,
    sm: 48,
    md: 80,
    lg: 120,
    xl: 160,
};

export const AvatarUpload = ({
    userName,
    currentImageUrl,
    size = 'lg',
    onImageSelect,
    isUploading = false,
    disabled = false,
}: AvatarUploadProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState<Position>({x: 0, y: 0});
    const [imageNaturalSize, setImageNaturalSize] = useState<{width: number; height: number} | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const dragStartPos = useRef<Position>({x: 0, y: 0});
    const dragStartOffset = useRef<Position>({x: 0, y: 0});

    const avatarSize = sizeMap[size];

    useEffect(() => {
        if (currentImageUrl && imageRef.current) {
            const img = new Image();
            img.onload = () => {
                setImageNaturalSize({width: img.width, height: img.height});
                const minDimension = Math.min(img.width, img.height);
                const scale = avatarSize / minDimension;
                const scaledWidth = img.width * scale;
                const scaledHeight = img.height * scale;
                setPosition({
                    x: (avatarSize - scaledWidth) / 2,
                    y: (avatarSize - scaledHeight) / 2,
                });
            };
            img.src = currentImageUrl;
        }
    }, [currentImageUrl, avatarSize]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!currentImageUrl || disabled || isUploading) return;
        e.preventDefault();
        setIsDragging(true);
        dragStartPos.current = {x: e.clientX, y: e.clientY};
        dragStartOffset.current = {...position};
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging || !currentImageUrl) return;
        const deltaX = e.clientX - dragStartPos.current.x;
        const deltaY = e.clientY - dragStartPos.current.y;
        setPosition({
            x: dragStartOffset.current.x + deltaX,
            y: dragStartOffset.current.y + deltaY,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, position]);

    const handleTouchStart = (e: React.TouchEvent) => {
        if (!currentImageUrl || disabled || isUploading) return;
        const touch = e.touches[0];
        setIsDragging(true);
        dragStartPos.current = {x: touch.clientX, y: touch.clientY};
        dragStartOffset.current = {...position};
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || !currentImageUrl) return;
        e.preventDefault();
        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStartPos.current.x;
        const deltaY = touch.clientY - dragStartPos.current.y;
        setPosition({
            x: dragStartOffset.current.x + deltaX,
            y: dragStartOffset.current.y + deltaY,
        });
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    const scale = imageNaturalSize
        ? avatarSize / Math.min(imageNaturalSize.width, imageNaturalSize.height)
        : 1;

    return (
        <Stack gap="sm">
            <Box
                ref={containerRef}
                style={{
                    width: avatarSize,
                    height: avatarSize,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 'var(--mantine-radius-md)',
                    cursor: currentImageUrl && !disabled && !isUploading ? 'move' : 'default',
                    userSelect: 'none',
                    border: '4px solid var(--mantine-color-body)',
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <LoadingOverlay visible={isUploading} />
                {currentImageUrl ? (
                    <img
                        ref={imageRef}
                        src={currentImageUrl}
                        alt={userName}
                        style={{
                            position: 'absolute',
                            width: imageNaturalSize ? imageNaturalSize.width * scale : 'auto',
                            height: imageNaturalSize ? imageNaturalSize.height * scale : 'auto',
                            left: position.x,
                            top: position.y,
                            pointerEvents: 'none',
                        }}
                        draggable={false}
                    />
                ) : (
                    <Avatar
                        userName={userName}
                        size={size}
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                    />
                )}
            </Box>
            <Group gap="xs">
                <Button
                    variant="light"
                    size="sm"
                    onClick={onImageSelect}
                    loading={isUploading}
                    disabled={disabled || isUploading}
                >
                    {currentImageUrl ? 'Change Image' : 'Choose Image'}
                </Button>
                {currentImageUrl && !disabled && !isUploading && (
                    <Text size="xs" c="dimmed">
                        Drag to reposition
                    </Text>
                )}
            </Group>
            <Text size="xs" c="dimmed">
                Recommended: Square image, max 2MB
            </Text>
        </Stack>
    );
};
